/**
 * Anmeldung — Kopie aus account-web, nicht neu geschrieben.
 *
 * Das ist die Lehre aus ADR-0003: dort wurde der Flow selbst gebaut und
 * scheiterte an geratenen Endpunktnamen.
 *
 * Die Sitzungs-Aufrufe (`/session`, `/session/refresh`, `/logout`) gehen in
 * Produktion an core-api: Der CI-Job `build-buchhaltung-web` setzt
 * `VITE_AUTH_SERVICE_URL=https://api.elbklar.com`, wie bei finance (BH-1,
 * Auftrag 010). Bis dahin stand dort `/api` — bei konto fuehrt das zu
 * core-api, hier aber zu buchhaltung-api, und die kennt keine dieser Routen.
 * Die Sitzungsabfrage lief in den 404, niemand konnte sich anmelden. Der
 * Rueckfall `/api` unten greift nur ohne Build-Variable (lokal, dort meist mit
 * `VITE_DEV_AUTH`).
 *
 * ACHTUNG, zweite Adresse: Der OAuth-Start laeuft NICHT same-origin, sondern
 * ueber `https://api.elbklar.com/login/google`. Grund ist die Rueckruf-URL:
 * `login_google` baut sie aus `X-Forwarded-Host` zusammen
 * (`core/auth/router.py`). Ueber `buchhaltung.elbklar.com/api/login/google`
 * entstuende `https://buchhaltung.elbklar.com/callback/google` — eine
 * Adresse, die weder in der Google Console eingetragen ist noch von Caddy zu
 * core-api fuehrt (dort liegt die SPA). Der Rueckruf muss auf dem Host
 * landen, der ihn ausgeloest hat. Fuer die Weiterleitung im Browser spielt
 * CORS ohnehin keine Rolle. Same-origin bleiben die Fach-Aufrufe an
 * buchhaltung-api (`VITE_API_URL=/api`); fuer die Sitzungs-Aufrufe an
 * core-api steht buchhaltung.elbklar.com in deren CORS-Liste.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import type { User } from '@/types';

// Rueckfall wie in finance-web: core-api, nicht `/api`. Mit `/api` lief jede
// Sitzungsanfrage in den 404 von buchhaltung-api (BH-1), sobald die
// Build-Variable fehlte; die Anmeldung endete in einer Schleife.
const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL || 'https://api.elbklar.com';
const AUTH_LOGIN_URL = import.meta.env.VITE_AUTH_LOGIN_URL || 'https://api.elbklar.com';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithGoogle: (redirectTo?: string) => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Dev mode mock user for testing
const DEV_MODE = import.meta.env.VITE_DEV_AUTH === 'true';

/**
 * Rolle und Adresse des Dev-Users sind einstellbar — und das ist kein
 * Beiwerk. Zwei der Regeln dieser Anwendung haengen genau daran: Rot gibt nur
 * `admin` frei, und die Pruefliste zeigt nur das eigene Postfach. Ohne
 * Schalter liesse sich die *andere* Seite dieser Regeln in einer Klickprobe
 * nie ansehen — man saehe immer nur die Ansicht des Admins und wuesste nicht,
 * was ein Mitarbeitender vorfindet. Wirkt ausschliesslich bei
 * `VITE_DEV_AUTH=true`.
 */
const MOCK_USER: User = {
  id: 'dev-user-123',
  email: import.meta.env.VITE_DEV_AUTH_EMAIL || 'dev@elbklar.local',
  name: import.meta.env.VITE_DEV_AUTH_NAME || 'Dev User',
  role: import.meta.env.VITE_DEV_AUTH_ROLE || 'admin',
  department: null,
  picture: null,
};

// Session check interval (1 minute)
const SESSION_CHECK_INTERVAL = 60000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    if (DEV_MODE) return MOCK_USER;
    return null;
  });
  const [isLoading, setIsLoading] = useState(!DEV_MODE);
  const [error] = useState<string | null>(null);
  const sessionCheckRef = useRef<number | null>(null);

  /**
   * Refresh session tokens.
   */
  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${AUTH_SERVICE_URL}/session/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  /**
   * Fetch session from auth service.
   * Uses credentials: 'include' to send HTTP-only cookies.
   */
  const fetchSession = useCallback(async (): Promise<User | null> => {
    const read = async (): Promise<User | null> => {
      const response = await fetch(`${AUTH_SERVICE_URL}/session`, {
        credentials: 'include',
      });
      if (!response.ok) return null;
      const session = await response.json();
      return {
        id: session.user_id,
        email: session.email,
        name: session.name,
        role: session.role,
        department: session.department || null,
        picture: session.picture || null,
      };
    };

    try {
      const user = await read();
      if (user) return user;
      // Abgelaufener Zugriffs-Token: einmal erneuern, dann erneut lesen.
      const refreshed = await refreshSession();
      return refreshed ? await read() : null;
    } catch {
      return null;
    }
  }, [refreshSession]);

  /**
   * Initialize auth state on mount.
   */
  useEffect(() => {
    if (DEV_MODE) return;

    let mounted = true;

    const initAuth = async () => {
      const sessionUser = await fetchSession();

      if (mounted) {
        setUser(sessionUser);
        setIsLoading(false);
      }
    };

    initAuth();

    // Set up periodic session check
    sessionCheckRef.current = window.setInterval(async () => {
      const sessionUser = await fetchSession();
      if (mounted) setUser(sessionUser);
    }, SESSION_CHECK_INTERVAL);

    return () => {
      mounted = false;
      if (sessionCheckRef.current) {
        window.clearInterval(sessionCheckRef.current);
      }
    };
  }, [fetchSession]);

  /**
   * Redirect to Google OAuth via centralized auth service.
   */
  const loginWithGoogle = useCallback((redirectTo: string = '/') => {
    // Core API braucht die volle URL, um zurueckspringen zu koennen.
    const fullRedirectUrl = redirectTo.startsWith('http')
      ? redirectTo
      : `${window.location.origin}${redirectTo}`;
    const loginUrl = `${AUTH_LOGIN_URL}/login/google?redirect_to=${encodeURIComponent(fullRedirectUrl)}`;
    window.location.href = loginUrl;
  }, []);

  /**
   * Logout - clears cookies via auth service.
   */
  const logout = useCallback(async () => {
    try {
      await fetch(`${AUTH_SERVICE_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors
    }

    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        loginWithGoogle,
        logout,
        refreshSession,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
